import { ReactNode, useEffect } from 'react';
import { usePullToRefresh } from '../../contexts/PullToRefreshContext';

interface PageLayoutProps {
  title: string | ReactNode;
  subtitle?: string | ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  showLogo?: boolean;
  /** Optional refresh handler - when provided, pull-to-refresh will trigger this function */
  onRefresh?: () => Promise<void>;
  /** Disable pull-to-refresh temporarily (e.g., while loading) */
  refreshDisabled?: boolean;
}

export default function PageLayout({
  title,
  subtitle,
  actions,
  children,
  showLogo = true,
  onRefresh,
  refreshDisabled: _refreshDisabled = false,
}: PageLayoutProps) {
  const { setRefreshHandler } = usePullToRefresh();

  // Register the refresh handler with the global pull-to-refresh context
  useEffect(() => {
    if (onRefresh) {
      setRefreshHandler(onRefresh);
    }
    return () => {
      setRefreshHandler(null);
    };
  }, [onRefresh, setRefreshHandler]);

  return (
    <div className="min-h-full">
      <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
        {/* Header — skipped entirely when there is nothing to show (empty
            title + no subtitle/actions/logo), so pages can go chrome-less. */}
        {(showLogo || title || subtitle || actions) && (
        <div className="p-4 sm:p-6 border-b border-gray-200">
          {showLogo && (
            <div className="flex justify-center md:justify-start mb-4">
              <img src="/grautomates-dark.png" alt="GRPS Logo" className="h-16" />
            </div>
          )}
          <div
            className="flex flex-col gap-4 xl:gap-3 xl:flex-row xl:items-start xl:justify-between"
          >
            <div className="min-w-0 flex-shrink">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight break-words">
                {title}
              </h1>
              {subtitle && <div className="mt-1 text-base text-gray-600">{subtitle}</div>}
            </div>
            {actions && (
              <div className="flex flex-wrap items-center gap-2 flex-shrink-0">{actions}</div>
            )}
          </div>
        </div>
        )}

        {/* Content */}
        <div className="p-4 sm:p-6">{children}</div>
      </div>
    </div>
  );
}
